import type { ShapeType, HumNote, ThemePreset, CanvasTransform, StyleParams } from '../types';
import type { EmotionLabel } from '../types';
import { EMOTION_COLORS } from '../ai/emotion';

const MIN_FREQ = 80;
const MAX_FREQ = 1000;
const COLOR_HZ = 10;
const COLOR_RESET_HZ = 180;

const shapePool: ShapeType[] = [
  'circle', 'triangle', 'diamond', 'pentagon', 'hexagon',
  'star4', 'star5', 'star6', 'star8', 'cross',
  'ring', 'arrow', 'squiggle', 'crescent', 'teardrop',
  'heart', 'leaf', 'blob1', 'blob2', 'blob3',
  'wave', 'spiral', 'zigzag', 'hourglass', 'bowtie',
  'flag', 'drop', 'cloud', 'moon', 'sun',
  'eye', 'diamond2', 'hexagon2', 'octagon', 'decagon',
  'starburst', 'flower', 'gear', 'lightning', 'flame',
  'feather', 'shell', 'crystal', 'prism', 'diamond3',
  'infinity', 'nova',
  'amoeba', 'dna', 'mandala', 'nebula', 'fractal',
  'doubleSpiral', 'concentric', 'interference',
  'vortex', 'ripple', 'helix', 'tessellation',
  'moire', 'fractalTree', 'plasma', 'orb',
  'lissajous', 'goldenSpiral', 'crystalBloom', 'quantumNode', 'stellarRing',
];

const emotionShapePools: Record<EmotionLabel, ShapeType[]> = {
  happy: ['flower', 'starburst', 'sun', 'heart', 'nova', 'star5', 'star6', 'star8', 'orb', 'mandala', 'cloud', 'spiral', 'crystalBloom', 'stellarRing'],
  sad: ['teardrop', 'crescent', 'drop', 'moon', 'feather', 'eye', 'shell', 'helix', 'doubleSpiral', 'spiral', 'goldenSpiral'],
  angry: ['lightning', 'zigzag', 'cross', 'arrow', 'flame', 'gear', 'star4', 'prism', 'hourglass', 'spiral', 'quantumNode'],
  fear: ['eye', 'zigzag', 'interference', 'vortex', 'moire', 'spiral', 'fractal', 'plasma', 'fractalTree', 'lissajous'],
  neutral: ['circle', 'ring', 'pentagon', 'hexagon', 'octagon', 'decagon', 'concentric', 'ripple', 'wave', 'diamond', 'spiral', 'crystalBloom'],
  surprise: ['nova', 'starburst', 'sun', 'flower', 'star8', 'star6', 'plasma', 'nebula', 'orb', 'concentric', 'spiral', 'quantumNode', 'stellarRing'],
  disgust: ['amoeba', 'blob1', 'blob2', 'blob3', 'squiggle', 'bowtie', 'flag', 'dna', 'helix', 'shell', 'spiral', 'lissajous'],
};

let shapeCounter = 0;

function emotionToPool(emotion: string | undefined): ShapeType[] {
  if (!emotion) return shapePool;
  const key = emotion as EmotionLabel;
  return emotionShapePools[key] ?? shapePool;
}

function nextShape(emotion?: string): ShapeType {
  const pool = emotionToPool(emotion);
  const shape = pool[shapeCounter % pool.length];
  shapeCounter++;
  return shape;
}

function emotionHueBias(emotion: string | undefined, freq: number): number {
  if (!emotion) return 0;
  const key = emotion as EmotionLabel;
  const ec = EMOTION_COLORS[key];
  if (!ec) return 0;

  const clamped = Math.max(MIN_FREQ, Math.min(MAX_FREQ, freq));
  const cyclePos = ((clamped - MIN_FREQ) % COLOR_RESET_HZ) / COLOR_RESET_HZ;
  const baseHue = cyclePos * 360;
  const emotionHue = ec.hue;

  const diff = ((emotionHue - baseHue + 540) % 360) - 180;
  return diff * 0.45;
}

function emotionSatMod(emotion: string | undefined): number {
  if (!emotion) return 0;
  const key = emotion as EmotionLabel;
  const ec = EMOTION_COLORS[key];
  if (!ec) return 0;
  return (ec.sat - 100) * 0.5;
}

function emotionLightMod(emotion: string | undefined): number {
  if (!emotion) return 0;
  const key = emotion as EmotionLabel;
  const ec = EMOTION_COLORS[key];
  if (!ec) return 0;
  return (ec.light - 60) * 0.3;
}

export function freqToColor(freq: number, theme: ThemePreset, emotion?: string): string {
  if (theme.name === 'mono') {
    const v = 55 + (Math.floor(freq / COLOR_HZ) % 5) * 9;
    return `hsl(0, 0%, ${v}%)`;
  }
  const clamped = Math.max(MIN_FREQ, Math.min(MAX_FREQ, freq));
  const cyclePos = ((clamped - MIN_FREQ) % COLOR_RESET_HZ) / COLOR_RESET_HZ;
  const hue = (cyclePos * 360 + theme.colorShift + emotionHueBias(emotion, freq) + 360) % 360;
  const sat = Math.max(30, Math.min(100, 100 + emotionSatMod(emotion)));
  const light = Math.max(40, Math.min(75, 60 + emotionLightMod(emotion)));
  return `hsl(${hue}, ${sat}%, ${light}%)`;
}

export function createNote(
  freq: number,
  amplitude: number,
  timestamp: number,
  duration: number,
  canvasW: number,
  canvasH: number,
  transform: CanvasTransform,
  theme: ThemePreset,
  id: number,
  emotion?: string,
  style?: StyleParams,
): HumNote {
  const shape = nextShape(emotion);
  const color = freqToColor(freq, theme, emotion);
  const s = style;
  const sizeVariance = s ? s.sizeVariance : 0.2;
  const spawnSpread = s ? s.spawnSpread : 0.38;
  const shapeScale = s ? s.shapeScale : 1.0;
  const alphaMult = s ? s.alphaMultiplier : 1.0;
  const baseSize = (55 + amplitude * 100) * shapeScale;
  const variance = 1 + (Math.random() - 0.5) * sizeVariance;
  const size = (baseSize * variance) / transform.scale;
  const spreadX = canvasW * spawnSpread;
  const spreadY = canvasH * spawnSpread;
  const rawX = canvasW / 2 + (Math.random() - 0.5) * 2 * spreadX;
  const rawY = canvasH / 2 + (Math.random() - 0.5) * 2 * spreadY;
  const margin = size * 0.6;
  const screenX = Math.max(margin, Math.min(canvasW - margin, rawX));
  const screenY = Math.max(margin, Math.min(canvasH - margin, rawY));
  const x = (screenX - transform.x) / transform.scale;
  const y = (screenY - transform.y) / transform.scale;

  return {
    id,
    frequency: freq,
    amplitude,
    shape,
    color,
    x,
    y,
    size,
    vx: 0,
    vy: 0,
    rotation: Math.random() * Math.PI * 2,
    rotationSpeed: s ? s.rotationSpeed : 0,
    alpha: Math.min(theme.shapeAlpha * alphaMult, 0.5),
    birthTime: performance.now(),
    timestamp,
    duration,
    trail: [],
    emotion,
    style: s,
  };
}

// ─── Pre-render cache ───
const shapeCache = new Map<string, HTMLCanvasElement>();

function getShapeKey(shape: ShapeType, size: number): string {
  const bucket = Math.round(size);
  return `${shape}_${bucket}`;
}

function getOrCreateCached(shape: ShapeType, size: number): HTMLCanvasElement {
  const key = getShapeKey(shape, size);
  let cached = shapeCache.get(key);
  if (cached) return cached;

  const pad = size * 0.3;
  const dim = (size + pad * 2);
  cached = document.createElement('canvas');
  cached.width = dim;
  cached.height = dim;
  const cctx = cached.getContext('2d')!;
  cctx.translate(dim / 2, dim / 2);
  const r = size * 0.35;

  const grad = cctx.createRadialGradient(-r * 0.15, -r * 0.15, 0, 0, 0, r * 1.2);
  grad.addColorStop(0, '#ffffff');
  grad.addColorStop(0.7, '#e0e0e0');
  grad.addColorStop(1, '#a0a0a0');
  cctx.fillStyle = grad;

  drawShapePath(cctx, shape, r);
  cctx.fill();

  cctx.globalAlpha = 0.35;
  drawShapeHighlight(cctx, shape, r);
  cctx.fill();

  shapeCache.set(key, cached);
  return cached;
}

const tintCanvas = document.createElement('canvas');
const tintCtx = tintCanvas.getContext('2d')!;

export function drawShape(
  ctx: CanvasRenderingContext2D,
  note: HumNote,
  alphaOverride?: number,
  glowIntensity?: number,
): void {
  const { x, y, size, shape, color, rotation, alpha: baseAlpha } = note;
  const alpha = alphaOverride ?? baseAlpha;
  const glow = glowIntensity ?? 1;

  const cached = getOrCreateCached(shape, size);

  if (glow > 0.5) {
    ctx.save();
    ctx.shadowColor = color;
    ctx.shadowBlur = size * 0.4 * glow;
  }

  tintCanvas.width = cached.width;
  tintCanvas.height = cached.height;
  tintCtx.clearRect(0, 0, tintCanvas.width, tintCanvas.height);
  tintCtx.globalCompositeOperation = 'source-over';
  tintCtx.drawImage(cached, 0, 0);
  tintCtx.globalCompositeOperation = 'source-in';
  tintCtx.fillStyle = color;
  tintCtx.fillRect(0, 0, tintCanvas.width, tintCanvas.height);

  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  ctx.globalAlpha = alpha;
  ctx.drawImage(tintCanvas, -tintCanvas.width / 2, -tintCanvas.height / 2);
  ctx.restore();

  if (glow > 0.5) {
    ctx.restore();
  }
}

export function drawShapeTinted(
  ctx: CanvasRenderingContext2D,
  shape: ShapeType,
  color: string,
  x: number,
  y: number,
  size: number,
  alpha: number,
  rotation: number,
): void {
  const cached = getOrCreateCached(shape, size);
  tintCanvas.width = cached.width;
  tintCanvas.height = cached.height;
  tintCtx.clearRect(0, 0, tintCanvas.width, tintCanvas.height);
  tintCtx.globalCompositeOperation = 'source-over';
  tintCtx.drawImage(cached, 0, 0);
  tintCtx.globalCompositeOperation = 'source-in';
  tintCtx.fillStyle = color;
  tintCtx.fillRect(0, 0, tintCanvas.width, tintCanvas.height);
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  ctx.globalAlpha = alpha;
  ctx.drawImage(tintCanvas, -tintCanvas.width / 2, -tintCanvas.height / 2);
  ctx.restore();
}

function drawShapePath(ctx: CanvasRenderingContext2D, shape: ShapeType, r: number): void {
  ctx.beginPath();
  switch (shape) {
    case 'circle':
      ctx.arc(0, 0, r, 0, Math.PI * 2);
      break;
    case 'triangle':
      ctx.moveTo(0, -r);
      ctx.lineTo(r * 0.866, r * 0.5);
      ctx.lineTo(-r * 0.866, r * 0.5);
      ctx.closePath();
      break;
    case 'diamond':
      ctx.moveTo(0, -r);
      ctx.lineTo(r * 0.6, 0);
      ctx.lineTo(0, r);
      ctx.lineTo(-r * 0.6, 0);
      ctx.closePath();
      break;
    case 'pentagon':
      drawPolygon(ctx, 5, r);
      break;
    case 'hexagon':
      drawPolygon(ctx, 6, r);
      break;
    case 'star4':
      drawStar(ctx, 4, r, r * 0.45);
      break;
    case 'star5':
      drawStar(ctx, 5, r, r * 0.45);
      break;
    case 'star6':
      drawStar(ctx, 6, r, r * 0.5);
      break;
    case 'star8':
      drawStar(ctx, 8, r, r * 0.55);
      break;
    case 'cross': {
      const t = r * 0.3;
      ctx.moveTo(-t, -r);
      ctx.lineTo(t, -r);
      ctx.lineTo(t, -t);
      ctx.lineTo(r, -t);
      ctx.lineTo(r, t);
      ctx.lineTo(t, t);
      ctx.lineTo(t, r);
      ctx.lineTo(-t, r);
      ctx.lineTo(-t, t);
      ctx.lineTo(-r, t);
      ctx.lineTo(-r, -t);
      ctx.lineTo(-t, -t);
      ctx.closePath();
      break;
    }
    case 'ring':
      ctx.arc(0, 0, r, 0, Math.PI * 2);
      ctx.moveTo(r * 0.6, 0);
      ctx.arc(0, 0, r * 0.6, 0, Math.PI * 2, true);
      break;
    case 'arrow':
      ctx.moveTo(0, -r);
      ctx.lineTo(r * 0.7, r * 0.3);
      ctx.lineTo(r * 0.3, r * 0.1);
      ctx.lineTo(r * 0.3, r);
      ctx.lineTo(-r * 0.3, r);
      ctx.lineTo(-r * 0.3, r * 0.1);
      ctx.lineTo(-r * 0.7, r * 0.3);
      ctx.closePath();
      break;
    case 'squiggle': {
      ctx.moveTo(-r, 0);
      const steps = 16;
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        ctx.lineTo(-r + t * 2 * r, Math.sin(t * Math.PI * 3) * r * 0.35);
      }
      for (let i = steps; i >= 0; i--) {
        const t = i / steps;
        ctx.lineTo(-r + t * 2 * r, Math.sin(t * Math.PI * 3) * r * 0.35 + r * 0.25);
      }
      ctx.closePath();
      break;
    }
    case 'crescent':
      ctx.arc(0, 0, r, 0, Math.PI * 2);
      ctx.moveTo(r * 0.4, 0);
      ctx.arc(r * 0.3, 0, r * 0.7, 0, Math.PI * 2, true);
      break;
    case 'teardrop':
      ctx.moveTo(0, -r);
      ctx.bezierCurveTo(r * 0.8, -r * 0.3, r * 0.6, r * 0.6, 0, r);
      ctx.bezierCurveTo(-r * 0.6, r * 0.6, -r * 0.8, -r * 0.3, 0, -r);
      break;
    case 'heart': {
      const topY = -r * 0.4;
      ctx.moveTo(0, r);
      ctx.bezierCurveTo(-r, r * 0.3, -r, topY - r * 0.3, 0, topY);
      ctx.bezierCurveTo(r, topY - r * 0.3, r, r * 0.3, 0, r);
      break;
    }
    case 'leaf':
      ctx.moveTo(0, -r);
      ctx.bezierCurveTo(r * 0.8, -r * 0.5, r * 0.8, r * 0.5, 0, r);
      ctx.bezierCurveTo(-r * 0.8, r * 0.5, -r * 0.8, -r * 0.5, 0, -r);
      break;
    case 'blob1':
      ctx.moveTo(0, -r);
      ctx.bezierCurveTo(r * 0.9, -r * 0.8, r * 1.1, r * 0.2, r * 0.3, r * 0.9);
      ctx.bezierCurveTo(-r * 0.2, r * 1.1, -r * 0.9, r * 0.5, -r * 0.7, -r * 0.1);
      ctx.bezierCurveTo(-r * 0.5, -r * 0.7, -r * 0.2, -r * 1.1, 0, -r);
      break;
    case 'blob2':
      ctx.moveTo(r * 0.2, -r);
      ctx.bezierCurveTo(r, -r * 0.6, r * 0.9, r * 0.5, r * 0.1, r * 0.9);
      ctx.bezierCurveTo(-r * 0.5, r * 1.1, -r, r * 0.3, -r * 0.8, -r * 0.3);
      ctx.bezierCurveTo(-r * 0.6, -r * 0.9, -r * 0.3, -r * 1.1, r * 0.2, -r);
      break;
    case 'blob3':
      ctx.moveTo(0, -r * 0.9);
      ctx.bezierCurveTo(r * 0.7, -r, r * 1.1, -r * 0.2, r * 0.8, r * 0.4);
      ctx.bezierCurveTo(r * 0.5, r, -r * 0.3, r * 1.1, -r * 0.7, r * 0.5);
      ctx.bezierCurveTo(-r * 1.1, 0, -r * 0.9, -r * 0.7, 0, -r * 0.9);
      break;
    case 'wave': {
      const wsteps = 14;
      ctx.moveTo(-r, 0);
      for (let i = 0; i <= wsteps; i++) {
        const t = i / wsteps;
        ctx.lineTo(-r + t * 2 * r, Math.sin(t * Math.PI * 2) * r * 0.4);
      }
      ctx.lineTo(r, r * 0.6);
      for (let i = wsteps; i >= 0; i--) {
        const t = i / wsteps;
        ctx.lineTo(-r + t * 2 * r, Math.sin(t * Math.PI * 2) * r * 0.4 + r * 0.3);
      }
      ctx.closePath();
      break;
    }
    case 'spiral': {
      ctx.moveTo(0, 0);
      const spiralSteps = 30;
      for (let i = 0; i <= spiralSteps; i++) {
        const t = i / spiralSteps;
        const angle = t * Math.PI * 4;
        const rad = t * r;
        ctx.lineTo(Math.cos(angle) * rad, Math.sin(angle) * rad);
      }
      break;
    }
    case 'zigzag': {
      ctx.moveTo(-r, -r * 0.5);
      const zz = 5;
      for (let i = 0; i <= zz; i++) {
        const sx = -r + (i / zz) * 2 * r;
        const sy = (i % 2 === 0 ? -r * 0.5 : r * 0.5);
        ctx.lineTo(sx, sy);
      }
      for (let i = zz; i >= 0; i--) {
        const sx = -r + (i / zz) * 2 * r;
        const sy = (i % 2 === 0 ? -r * 0.3 : r * 0.7);
        ctx.lineTo(sx, sy);
      }
      ctx.closePath();
      break;
    }
    case 'hourglass':
      ctx.moveTo(-r, -r);
      ctx.lineTo(r, -r);
      ctx.lineTo(-r * 0.15, 0);
      ctx.lineTo(r, r);
      ctx.lineTo(-r, r);
      ctx.lineTo(r * 0.15, 0);
      ctx.closePath();
      break;
    case 'bowtie':
      ctx.moveTo(0, 0);
      ctx.lineTo(-r, -r * 0.7);
      ctx.lineTo(-r, r * 0.7);
      ctx.closePath();
      ctx.moveTo(0, 0);
      ctx.lineTo(r, -r * 0.7);
      ctx.lineTo(r, r * 0.7);
      ctx.closePath();
      break;
    case 'flag':
      ctx.moveTo(-r * 0.15, -r);
      ctx.lineTo(-r * 0.15, r);
      ctx.moveTo(-r * 0.1, -r);
      ctx.bezierCurveTo(r * 0.3, -r * 0.8, r * 0.5, -r * 0.2, r, -r * 0.3);
      ctx.lineTo(r * 0.3, 0);
      ctx.bezierCurveTo(r * 0.5, r * 0.2, r * 0.3, r * 0.8, -r * 0.1, r);
      break;
    case 'drop':
      ctx.moveTo(0, -r);
      ctx.bezierCurveTo(r * 0.8, r * 0.1, r * 0.5, r * 0.8, 0, r);
      ctx.bezierCurveTo(-r * 0.5, r * 0.8, -r * 0.8, r * 0.1, 0, -r);
      break;
    case 'cloud':
      ctx.arc(-r * 0.3, r * 0.1, r * 0.45, 0, Math.PI * 2);
      ctx.arc(r * 0.2, -r * 0.1, r * 0.55, 0, Math.PI * 2);
      ctx.arc(r * 0.6, r * 0.2, r * 0.35, 0, Math.PI * 2);
      ctx.arc(-r * 0.5, r * 0.3, r * 0.35, 0, Math.PI * 2);
      break;
    case 'moon':
      ctx.arc(0, 0, r, -Math.PI * 0.5, Math.PI * 0.5);
      ctx.bezierCurveTo(r * 0.2, r * 0.3, r * 0.2, -r * 0.3, 0, -r);
      break;
    case 'sun':
      ctx.arc(0, 0, r * 0.5, 0, Math.PI * 2);
      break;
    case 'eye':
      ctx.moveTo(-r, 0);
      ctx.bezierCurveTo(-r * 0.5, -r * 0.7, r * 0.5, -r * 0.7, r, 0);
      ctx.bezierCurveTo(r * 0.5, r * 0.7, -r * 0.5, r * 0.7, -r, 0);
      ctx.closePath();
      break;
    case 'diamond2':
      ctx.moveTo(0, -r);
      ctx.lineTo(r * 0.7, 0);
      ctx.lineTo(0, r * 0.6);
      ctx.lineTo(-r * 0.7, 0);
      ctx.closePath();
      break;
    case 'hexagon2':
      drawPolygon(ctx, 6, r * 0.85);
      break;
    case 'octagon':
      drawPolygon(ctx, 8, r);
      break;
    case 'decagon':
      drawPolygon(ctx, 10, r);
      break;
    case 'starburst':
      drawStar(ctx, 12, r, r * 0.65);
      break;
    case 'flower': {
      for (let i = 0; i < 6; i++) {
        const angle = (i / 6) * Math.PI * 2;
        ctx.ellipse(
          Math.cos(angle) * r * 0.35,
          Math.sin(angle) * r * 0.35,
          r * 0.4, r * 0.2, angle, 0, Math.PI * 2,
        );
      }
      break;
    }
    case 'gear': {
      const teeth = 8;
      const innerR = r * 0.65;
      const outerR = r;
      ctx.moveTo(outerR, 0);
      for (let i = 0; i < teeth; i++) {
        const a1 = (i / teeth) * Math.PI * 2;
        const a2 = ((i + 0.35) / teeth) * Math.PI * 2;
        const a3 = ((i + 0.5) / teeth) * Math.PI * 2;
        const a4 = ((i + 0.85) / teeth) * Math.PI * 2;
        ctx.lineTo(Math.cos(a1) * outerR, Math.sin(a1) * outerR);
        ctx.lineTo(Math.cos(a2) * outerR, Math.sin(a2) * outerR);
        ctx.lineTo(Math.cos(a3) * innerR, Math.sin(a3) * innerR);
        ctx.lineTo(Math.cos(a4) * innerR, Math.sin(a4) * innerR);
      }
      ctx.closePath();
      break;
    }
    case 'lightning':
      ctx.moveTo(-r * 0.2, -r);
      ctx.lineTo(r * 0.3, -r * 0.15);
      ctx.lineTo(r * 0.05, -r * 0.15);
      ctx.lineTo(r * 0.4, r);
      ctx.lineTo(-r * 0.1, r * 0.2);
      ctx.lineTo(r * 0.1, r * 0.2);
      ctx.closePath();
      break;
    case 'flame':
      ctx.moveTo(0, -r);
      ctx.bezierCurveTo(r * 0.6, -r * 0.4, r * 0.8, r * 0.2, r * 0.3, r * 0.7);
      ctx.bezierCurveTo(r * 0.1, r, -r * 0.1, r, -r * 0.3, r * 0.7);
      ctx.bezierCurveTo(-r * 0.8, r * 0.2, -r * 0.6, -r * 0.4, 0, -r);
      break;
    case 'feather':
      ctx.moveTo(0, -r);
      ctx.bezierCurveTo(r * 0.3, -r * 0.6, r * 0.7, -r * 0.2, r * 0.5, r * 0.3);
      ctx.bezierCurveTo(r * 0.3, r * 0.7, r * 0.1, r, 0, r);
      ctx.bezierCurveTo(-r * 0.1, r, -r * 0.3, r * 0.7, -r * 0.5, r * 0.3);
      ctx.bezierCurveTo(-r * 0.7, -r * 0.2, -r * 0.3, -r * 0.6, 0, -r);
      break;
    case 'shell': {
      ctx.moveTo(0, r);
      const shellSteps = 24;
      for (let i = 0; i <= shellSteps; i++) {
        const t = i / shellSteps;
        const angle = t * Math.PI * 3;
        const rad = r * 0.2 + t * r * 0.7;
        ctx.lineTo(Math.cos(angle) * rad * 0.5, -r + t * 2 * r + Math.sin(angle) * rad * 0.15);
      }
      ctx.closePath();
      break;
    }
    case 'crystal':
      ctx.moveTo(0, -r);
      ctx.lineTo(r * 0.35, -r * 0.3);
      ctx.lineTo(r * 0.5, r * 0.2);
      ctx.lineTo(r * 0.2, r);
      ctx.lineTo(-r * 0.2, r);
      ctx.lineTo(-r * 0.5, r * 0.2);
      ctx.lineTo(-r * 0.35, -r * 0.3);
      ctx.closePath();
      break;
    case 'prism':
      ctx.moveTo(0, -r);
      ctx.lineTo(r * 0.866, r * 0.5);
      ctx.lineTo(-r * 0.866, r * 0.5);
      ctx.closePath();
      break;
    case 'diamond3':
      ctx.moveTo(0, -r);
      ctx.lineTo(r * 0.45, -r * 0.3);
      ctx.lineTo(r * 0.45, r * 0.3);
      ctx.lineTo(0, r);
      ctx.lineTo(-r * 0.45, r * 0.3);
      ctx.lineTo(-r * 0.45, -r * 0.3);
      ctx.closePath();
      break;
    case 'infinity': {
      const s = r * 0.6;
      ctx.moveTo(-s, 0);
      ctx.bezierCurveTo(-s, -s, 0, -s, 0, 0);
      ctx.bezierCurveTo(0, s, s, s, s, 0);
      ctx.bezierCurveTo(s, -s, 0, -s, 0, 0);
      ctx.bezierCurveTo(0, s, -s, s, -s, 0);
      break;
    }
    case 'nova':
      drawStar(ctx, 16, r, r * 0.75);
      break;
    // ─── New abstract shapes ───
    case 'amoeba': {
      const pts = 8;
      for (let i = 0; i < pts; i++) {
        const angle = (i / pts) * Math.PI * 2;
        const nextAngle = ((i + 1) / pts) * Math.PI * 2;
        const r1 = r * (0.6 + Math.sin(i * 2.1) * 0.35);
        const r2 = r * (0.6 + Math.sin((i + 1) * 2.1) * 0.35);
        const cp1a = angle + (nextAngle - angle) * 0.5;
        const cp1r = r * (0.8 + Math.cos(i * 3.7) * 0.3);
        if (i === 0) ctx.moveTo(Math.cos(angle) * r1, Math.sin(angle) * r1);
        ctx.quadraticCurveTo(Math.cos(cp1a) * cp1r, Math.sin(cp1a) * cp1r, Math.cos(nextAngle) * r2, Math.sin(nextAngle) * r2);
      }
      ctx.closePath();
      break;
    }
    case 'dna': {
      for (let strand = 0; strand < 2; strand++) {
        const offset = strand * Math.PI;
        ctx.moveTo(-r, Math.sin(offset) * r * 0.3);
        for (let i = 0; i <= 20; i++) {
          const t = i / 20;
          const x = -r + t * 2 * r;
          const y = Math.sin(t * Math.PI * 3 + offset) * r * 0.3;
          ctx.lineTo(x, y);
        }
      }
      for (let i = 0; i < 6; i++) {
        const t = (i + 0.5) / 6;
        const x = -r + t * 2 * r;
        const y1 = Math.sin(t * Math.PI * 3) * r * 0.3;
        const y2 = Math.sin(t * Math.PI * 3 + Math.PI) * r * 0.3;
        ctx.moveTo(x, y1);
        ctx.lineTo(x, y2);
      }
      break;
    }
    case 'mandala': {
      const rays = 12;
      for (let i = 0; i < rays; i++) {
        const angle = (i / rays) * Math.PI * 2;
        ctx.moveTo(0, 0);
        ctx.lineTo(Math.cos(angle) * r, Math.sin(angle) * r);
      }
      ctx.arc(0, 0, r * 0.5, 0, Math.PI * 2);
      ctx.arc(0, 0, r * 0.25, 0, Math.PI * 2);
      break;
    }
    case 'nebula': {
      const blobs = 5;
      for (let i = 0; i < blobs; i++) {
        const angle = (i / blobs) * Math.PI * 2;
        const bx = Math.cos(angle) * r * 0.3;
        const by = Math.sin(angle) * r * 0.3;
        const br = r * (0.3 + Math.sin(i * 1.7) * 0.15);
        ctx.moveTo(bx + br, by);
        ctx.arc(bx, by, br, 0, Math.PI * 2);
      }
      ctx.arc(0, 0, r * 0.2, 0, Math.PI * 2);
      break;
    }
    case 'fractal': {
      drawSierpinski(ctx, 0, -r, r * 1.732, 3);
      break;
    }
    case 'doubleSpiral': {
      for (let s = 0; s < 2; s++) {
        const offset = s * Math.PI;
        ctx.moveTo(0, 0);
        for (let i = 0; i <= 25; i++) {
          const t = i / 25;
          const angle = t * Math.PI * 3 + offset;
          const rad = t * r;
          ctx.lineTo(Math.cos(angle) * rad, Math.sin(angle) * rad);
        }
      }
      break;
    }
    case 'concentric': {
      for (let i = 1; i <= 4; i++) {
        ctx.arc(0, 0, r * (i / 4), 0, Math.PI * 2);
      }
      break;
    }
    case 'interference': {
      const cx2 = 0;
      const cy2 = 0;
      for (let i = 0; i <= 40; i++) {
        const angle = (i / 40) * Math.PI * 2;
        const wobble = Math.sin(angle * 6) * r * 0.15 + Math.cos(angle * 4) * r * 0.1;
        const rad = r * 0.6 + wobble;
        const x2 = cx2 + Math.cos(angle) * rad;
        const y2 = cy2 + Math.sin(angle) * rad;
        if (i === 0) ctx.moveTo(x2, y2);
        else ctx.lineTo(x2, y2);
      }
      ctx.closePath();
      break;
    }
    case 'vortex': {
      for (let arm = 0; arm < 3; arm++) {
        const offset = (arm / 3) * Math.PI * 2;
        ctx.moveTo(0, 0);
        for (let i = 0; i <= 20; i++) {
          const t = i / 20;
          const angle = t * Math.PI * 4 + offset;
          const rad = t * r;
          ctx.lineTo(Math.cos(angle) * rad, Math.sin(angle) * rad);
        }
      }
      break;
    }
    case 'ripple': {
      for (let i = 1; i <= 5; i++) {
        ctx.arc(0, 0, r * (i / 5) * 0.9, 0, Math.PI * 2);
      }
      break;
    }
    case 'helix': {
      for (let strand = 0; strand < 2; strand++) {
        const phase = strand * Math.PI;
        ctx.moveTo(-r, Math.sin(phase) * r * 0.25);
        for (let i = 0; i <= 24; i++) {
          const t = i / 24;
          const x = -r + t * 2 * r;
          const y = Math.sin(t * Math.PI * 4 + phase) * r * 0.25;
          ctx.lineTo(x, y);
        }
      }
      for (let i = 0; i < 8; i++) {
        const t = (i + 0.5) / 8;
        const x = -r + t * 2 * r;
        const y1 = Math.sin(t * Math.PI * 4) * r * 0.25;
        const y2 = Math.sin(t * Math.PI * 4 + Math.PI) * r * 0.25;
        ctx.moveTo(x, y1);
        ctx.lineTo(x, y2);
      }
      break;
    }
    case 'tessellation': {
      const s = r * 0.6;
      for (let row = -1; row <= 1; row++) {
        for (let col = -1; col <= 1; col++) {
          const ox = col * s * 1.732;
          const oy = row * s * 1.5 + (col % 2 !== 0 ? s * 0.75 : 0);
          ctx.moveTo(ox, oy - s * 0.5);
          ctx.lineTo(ox + s * 0.5, oy);
          ctx.lineTo(ox, oy + s * 0.5);
          ctx.lineTo(ox - s * 0.5, oy);
          ctx.closePath();
        }
      }
      break;
    }
    case 'moire': {
      const rings = 8;
      for (let i = 1; i <= rings; i++) {
        ctx.arc(0, 0, r * (i / rings), 0, Math.PI * 2);
      }
      for (let i = 0; i < 12; i++) {
        const angle = (i / 12) * Math.PI * 2;
        ctx.moveTo(0, 0);
        ctx.lineTo(Math.cos(angle) * r, Math.sin(angle) * r);
      }
      break;
    }
    case 'fractalTree': {
      drawTreeBranch(ctx, 0, r * 0.8, -Math.PI / 2, r * 0.45, 3);
      break;
    }
    case 'plasma': {
      const pts = 6;
      for (let i = 0; i < pts; i++) {
        const angle = (i / pts) * Math.PI * 2;
        const nextAngle = ((i + 1) / pts) * Math.PI * 2;
        const r1 = r * (0.5 + Math.sin(i * 1.3) * 0.3);
        const r2 = r * (0.5 + Math.sin((i + 1) * 1.3) * 0.3);
        const cpx = Math.cos((angle + nextAngle) / 2) * r * 0.8;
        const cpy = Math.sin((angle + nextAngle) / 2) * r * 0.8;
        if (i === 0) ctx.moveTo(Math.cos(angle) * r1, Math.sin(angle) * r1);
        ctx.quadraticCurveTo(cpx, cpy, Math.cos(nextAngle) * r2, Math.sin(nextAngle) * r2);
      }
      ctx.closePath();
      break;
    }
    case 'orb': {
      ctx.arc(0, 0, r * 0.7, 0, Math.PI * 2);
      ctx.moveTo(r * 0.3, 0);
      ctx.arc(0, 0, r * 0.3, 0, Math.PI * 2);
      ctx.ellipse(0, 0, r, r * 0.3, 0, 0, Math.PI * 2);
      break;
    }
    case 'lissajous': {
      const steps = 40;
      for (let i = 0; i <= steps; i++) {
        const t = (i / steps) * Math.PI * 2;
        const x = Math.sin(3 * t) * r;
        const y = Math.sin(2 * t) * r;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      break;
    }
    case 'goldenSpiral': {
      const steps = 40;
      for (let i = 0; i <= steps; i++) {
        const t = (i / steps) * Math.PI * 5;
        const rad = r * Math.exp(0.12 * t) * 0.2;
        const x = Math.cos(t) * rad;
        const y = Math.sin(t) * rad;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      break;
    }
    case 'crystalBloom': {
      const petals = 8;
      for (let i = 0; i < petals; i++) {
        const angle = (i / petals) * Math.PI * 2;
        ctx.moveTo(0, 0);
        ctx.quadraticCurveTo(Math.cos(angle - 0.3) * r * 0.8, Math.sin(angle - 0.3) * r * 0.8, Math.cos(angle) * r, Math.sin(angle) * r);
        ctx.quadraticCurveTo(Math.cos(angle + 0.3) * r * 0.8, Math.sin(angle + 0.3) * r * 0.8, 0, 0);
      }
      break;
    }
    case 'quantumNode': {
      ctx.arc(0, 0, r * 0.3, 0, Math.PI * 2);
      for (let i = 0; i < 4; i++) {
        const angle = (i / 4) * Math.PI * 2;
        const nx = Math.cos(angle) * r * 0.8;
        const ny = Math.sin(angle) * r * 0.8;
        ctx.moveTo(nx + r * 0.2, ny);
        ctx.arc(nx, ny, r * 0.2, 0, Math.PI * 2);
      }
      break;
    }
    case 'stellarRing': {
      ctx.arc(0, 0, r, 0, Math.PI * 2);
      ctx.moveTo(r * 0.7, 0);
      ctx.arc(0, 0, r * 0.7, 0, Math.PI * 2, true);
      for (let i = 0; i < 6; i++) {
        const angle = (i / 6) * Math.PI * 2;
        ctx.moveTo(Math.cos(angle) * r * 0.7, Math.sin(angle) * r * 0.7);
        ctx.lineTo(Math.cos(angle) * r, Math.sin(angle) * r);
      }
      break;
    }
  }
}

function drawSierpinski(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, depth: number): void {
  if (depth <= 0) {
    const h = size * 0.866;
    ctx.moveTo(x, y);
    ctx.lineTo(x + size / 2, y + h);
    ctx.lineTo(x - size / 2, y + h);
    ctx.closePath();
    return;
  }
  const half = size / 2;
  const h = size * 0.866;
  const halfH = h / 2;
  drawSierpinski(ctx, x, y, half, depth - 1);
  drawSierpinski(ctx, x - half / 2, y + halfH, half, depth - 1);
  drawSierpinski(ctx, x + half / 2, y + halfH, half, depth - 1);
}

function drawTreeBranch(ctx: CanvasRenderingContext2D, x: number, y: number, angle: number, len: number, depth: number): void {
  if (depth <= 0 || len < 2) return;
  const endX = x + Math.cos(angle) * len;
  const endY = y + Math.sin(angle) * len;
  ctx.moveTo(x, y);
  ctx.lineTo(endX, endY);
  const spread = 0.4 + Math.random() * 0.2;
  drawTreeBranch(ctx, endX, endY, angle - spread, len * 0.68, depth - 1);
  drawTreeBranch(ctx, endX, endY, angle + spread, len * 0.68, depth - 1);
}

function drawShapeHighlight(ctx: CanvasRenderingContext2D, shape: ShapeType, r: number): void {
  ctx.beginPath();
  switch (shape) {
    case 'circle':
    case 'ring':
    case 'flower':
    case 'gear':
    case 'starburst':
    case 'nova':
    case 'nebula':
    case 'concentric':
      ctx.arc(-r * 0.15, -r * 0.15, r * 0.25, 0, Math.PI * 2);
      break;
    case 'triangle':
    case 'prism':
    case 'fractal':
      ctx.arc(0, -r * 0.15, r * 0.15, 0, Math.PI * 2);
      break;
    case 'diamond':
    case 'diamond2':
    case 'diamond3':
    case 'crystal':
      ctx.arc(0, -r * 0.2, r * 0.12, 0, Math.PI * 2);
      break;
    default:
      ctx.arc(-r * 0.1, -r * 0.1, r * 0.18, 0, Math.PI * 2);
      break;
  }
}

function drawPolygon(ctx: CanvasRenderingContext2D, sides: number, r: number): void {
  for (let i = 0; i <= sides; i++) {
    const angle = (i / sides) * Math.PI * 2 - Math.PI / 2;
    const x = Math.cos(angle) * r;
    const y = Math.sin(angle) * r;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
}

function drawStar(ctx: CanvasRenderingContext2D, points: number, outerR: number, innerR: number): void {
  for (let i = 0; i <= points * 2; i++) {
    const angle = (i / (points * 2)) * Math.PI * 2 - Math.PI / 2;
    const rad = i % 2 === 0 ? outerR : innerR;
    const x = Math.cos(angle) * rad;
    const y = Math.sin(angle) * rad;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
}

export function clearShapeCache(): void {
  shapeCache.clear();
}
